/**
 * useCrmWebSocket - React hook for the CRM WebSocket connection.
 *
 * Manages the CrmWebSocket lifecycle within React, connecting/disconnecting
 * based on org ID and component lifecycle.
 *
 * Mounts once at the TeamProvider level, providing real-time CRM event
 * streaming alongside the existing polling mechanism.
 */

import { QueryClientContext } from '@tanstack/react-query';
import type { RefObject } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { EntityTypeName } from '../core/types';
import {
  invalidateCrmQueries,
  wasCrmInvalidatedRecently,
} from '../hooks/useCRMEventPolling';
import { refreshStoreEntities } from '../object-cards/entityFetchBatcher';
import { getCompleteObjectIds } from '../store';
import { applyCrmEventBatch } from './applyEvent';
import { CrmWebSocket } from './CrmWebSocket';
import { affectsCurrentUserPermissions } from './permissionEvents';
import type { CrmWebSocketEvent, CrmWebSocketStatus } from './types';

interface UseCrmWebSocketOptions {
  /** Org ID to connect to */
  orgId: number | null;
  /** Whether WebSocket should be enabled (default: false — callers must opt in explicitly) */
  enabled?: boolean;
  /**
   * Called (debounced, see PERMISSIONS_CHANGED_DEBOUNCE_MS) after a batch
   * contains an event that can change the current user's effective
   * permissions — a role or permission-group change, or a role-membership
   * association. The caller refetches its permission snapshot in response.
   * Must be a stable reference (useCallback).
   */
  onPermissionsChanged?: () => void;
}

/**
 * Trailing debounce for `onPermissionsChanged`. The backend's custom-object
 * access grant lands as two separate commits ~150ms apart (the
 * `org_custom_object_access` group is created, then every `ahoy_role` is
 * updated to include it), each broadcast as its own WS batch. Coalescing them
 * into one callback matters because the consumer's refresh (`loadTeams`)
 * drops a call that arrives while a previous one is in flight — firing per
 * batch would refetch on the first commit and silently skip the second, which
 * is the one that actually carries the grant.
 */
const PERMISSIONS_CHANGED_DEBOUNCE_MS = 500;

/**
 * Window (ms) within which handleFullRefreshRequired skips its own
 * invalidateCrmQueries call if one already ran — e.g. the poller's
 * zombie-WS escalation invalidates and force-reconnects, and the reconnect
 * itself triggers a `full_refresh_required` message moments later. Without
 * this, both fire and produce two invalidation waves instead of one.
 * Matches the poller's 30s base interval (the cadence a forced reconnect's
 * response would typically land within).
 */
const FULL_REFRESH_INVALIDATION_COALESCE_WINDOW_MS = 30000;

interface UseCrmWebSocketReturn {
  /** Current WebSocket connection status */
  status: CrmWebSocketStatus;
  /**
   * Ref holding the last received event ID.
   * Updated synchronously in the WS event handler so the poller
   * can read the latest value without waiting for a React render cycle.
   */
  lastEventIdRef: RefObject<number | null>;
  /** Subscribe to restricted entity types. Sends subscribe message to server. */
  subscribe: (entityTypes: EntityTypeName[]) => void;
  /** Unsubscribe from all restricted entity types. */
  unsubscribe: () => void;
  /**
   * Forces the WebSocket to reconnect now, bypassing the backoff schedule.
   * Safe no-op if there's no active connection or a reconnect attempt is
   * already in flight (status === 'connecting'). Does not reset the
   * reconnect-attempt counter, so a failed forced reconnect falls back to
   * the normal backoff schedule rather than looping tightly.
   */
  forceReconnect: () => void;
}

/**
 * React hook that manages a CRM WebSocket connection.
 *
 * When events arrive, they are applied to the React Query cache via
 * applyCrmEventBatch. When a full refresh is required (too many missed
 * events or long inactivity), all queries are invalidated.
 *
 * Usage (in TeamProvider):
 * ```tsx
 * const { lastEventIdRef } = useCrmWebSocket({
 *   orgId: currentOrg?.id ?? null,
 *   enabled: !!currentOrg?.id,
 * });
 * useCRMEventPolling({ wsLastEventIdRef: lastEventIdRef });
 * ```
 */
export function useCrmWebSocket({
  orgId,
  enabled = false,
  onPermissionsChanged,
}: UseCrmWebSocketOptions): UseCrmWebSocketReturn {
  // Use useContext directly (same pattern as useCRMEventPolling) to avoid
  // throwing when no QueryClientProvider exists in test contexts.
  const queryClient = useContext(QueryClientContext);

  const [status, setStatus] = useState<CrmWebSocketStatus>('disconnected');
  // Use a ref (not state) for lastEventId so it updates synchronously
  // in handleEvents. This prevents a race condition where the poller
  // fires between the WS event and the next React render, sees an
  // outdated wsLastEventId, and invalidates all queries.
  const lastEventIdRef = useRef<number | null>(null);
  const wsRef = useRef<CrmWebSocket | null>(null);
  // Track desired subscription at the hook level so it survives WS
  // disabled→enabled transitions. When a new CrmWebSocket instance is
  // created (e.g., org switch), the pending subscription is applied
  // immediately, preventing lost subscriptions.
  const pendingSubscriptionRef = useRef<EntityTypeName[] | null>(null);
  // Pending trailing-debounce timer for onPermissionsChanged. Held in a ref
  // (commit-phase state, never read during render) so a burst of permission
  // batches collapses into one callback and an org switch / unmount can
  // cancel it before the stale org's refresh fires.
  const permissionsChangedTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const schedulePermissionsChanged = useCallback(() => {
    if (!onPermissionsChanged) {
      return;
    }
    if (permissionsChangedTimerRef.current !== null) {
      clearTimeout(permissionsChangedTimerRef.current);
    }
    permissionsChangedTimerRef.current = setTimeout(() => {
      permissionsChangedTimerRef.current = null;
      onPermissionsChanged();
    }, PERMISSIONS_CHANGED_DEBOUNCE_MS);
  }, [onPermissionsChanged]);

  const handleEvents = useCallback(
    (events: CrmWebSocketEvent[]) => {
      if (!queryClient || !orgId) {
        return;
      }

      // Apply events to all cache layers (detail, list, slideout) in-place
      // via setQueryData. No invalidation needed — the cache mutations
      // directly trigger component re-renders.
      applyCrmEventBatch(queryClient, orgId, events);

      // Role / permission-group changes aren't CRM objects the store renders;
      // their effect is on `currentOrg.permissions`, which only loadTeams()
      // refreshes. Hand that off to the caller (debounced).
      if (affectsCurrentUserPermissions(events)) {
        schedulePermissionsChanged();
      }

      // Track last event ID synchronously (ref, not state) so the poller
      // can read the latest value immediately without waiting for a render.
      if (events.length > 0) {
        // Use reduce to avoid RangeError with large batches (Math.max spread exceeds call stack limit)
        const maxId = events.reduce(
          (max, e) => Math.max(max, e.event_id),
          -Infinity
        );
        if (lastEventIdRef.current === null || maxId > lastEventIdRef.current) {
          lastEventIdRef.current = maxId;
        }
      }
    },
    [queryClient, orgId, schedulePermissionsChanged]
  );

  const handleFullRefreshRequired = useCallback(() => {
    if (!queryClient || !orgId) {
      return;
    }

    // Deliberately do NOT clear the centralized store here.
    //
    // Clearing the store on wake-from-sleep causes a thundering-herd problem:
    // every mounted `useCrmObjectSnapshot` subscriber sees its object flip to
    // `undefined`, which sets `needsFetch = true` and calls `ensureEntity()`.
    // With dozens of entity badges on screen (meeting prep, slideout
    // associations, pipeline cards) this fires 30-40+ simultaneous fetch
    // requests, overwhelming the backend and causing cascading failures.
    //
    // Instead, we keep stale data visible (stale-while-revalidate) and let
    // React Query's invalidation handle the refresh. `invalidateQueries`
    // defaults to `refetchType: 'active'`, so only queries with mounted
    // observers refetch immediately — visible data gets fresh first. Inactive
    // (unmounted) queries are merely marked stale and refetch lazily on next
    // access. The store's `upsertObject` merge logic correctly applies the
    // fresh server data when those refetches complete.
    //
    // The store IS still cleared on org switch (crm-object-list-page.tsx)
    // where cross-org data leakage is the concern, not staleness.

    // Scope invalidation to CRM-related queries only.
    // A nuclear invalidateQueries() with no args would refetch ALL queries
    // (reasoning, files, user prefs, etc.) — wasteful and disruptive.
    // Skip if a recent invalidation (e.g. the poller's zombie-WS escalation
    // that triggered this very reconnect) already covered it — see
    // FULL_REFRESH_INVALIDATION_COALESCE_WINDOW_MS.
    if (
      !wasCrmInvalidatedRecently(FULL_REFRESH_INVALIDATION_COALESCE_WINDOW_MS)
    ) {
      invalidateCrmQueries(queryClient);
    }

    // React Query invalidation above only refreshes data backed by a mounted
    // query observer. Objects hydrated directly into the CRM store via
    // `ensureEntity` (entity badges, object cards) have NO query observer, so
    // invalidation never touches them — without an explicit re-fetch they keep
    // stale or deleted data indefinitely after missed-event recovery. Re-fetch
    // the complete store entries in batched chunks (associations skipped) and
    // drop any the server no longer returns (deleted / access-revoked). This is
    // bounded to objects already in the store, so it does not re-introduce the
    // thundering herd that clearing the store caused.
    void refreshStoreEntities(orgId, getCompleteObjectIds());

    // Do NOT reset lastEventIdRef here. The WebSocket is still connected
    // and active. Clearing the cursor would make the poller treat the WS
    // as inactive, triggering another full invalidation on the next event.
    // The invalidateQueries above already triggered refetches, and the WS
    // will continue tracking new events normally via handleEvents.
  }, [queryClient, orgId]);

  const subscribe = useCallback((entityTypes: EntityTypeName[]) => {
    pendingSubscriptionRef.current = entityTypes;
    wsRef.current?.subscribe(entityTypes);
  }, []);

  const unsubscribe = useCallback(() => {
    pendingSubscriptionRef.current = null;
    wsRef.current?.unsubscribe();
  }, []);

  const forceReconnect = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) {
      return;
    }
    // connect() unconditionally disconnects first, so calling it mid-attempt
    // would restart a handshake that might have been about to succeed —
    // skip while one is already in flight.
    if (ws.getStatus() === 'connecting') {
      return;
    }
    ws.connect();
  }, []);

  useEffect(() => {
    // Skip if no queryClient (no QueryClientProvider), disabled, or no org
    if (!queryClient || !enabled || !orgId) {
      // Clean up existing connection if conditions changed
      if (wsRef.current) {
        wsRef.current.destroy();
        wsRef.current = null;
        setStatus('disconnected');
      }
      return;
    }

    // Create and connect the WebSocket
    const ws = new CrmWebSocket({
      orgId,
      onEvents: handleEvents,
      onFullRefreshRequired: handleFullRefreshRequired,
      onStatusChange: setStatus,
    });

    wsRef.current = ws;

    // Apply any subscription that was requested while the WS was disabled.
    // This ensures CrmWebSocket.currentSubscription is set before connect(),
    // so the onopen handler re-sends it to the server.
    if (pendingSubscriptionRef.current !== null) {
      ws.subscribe(pendingSubscriptionRef.current);
    }

    ws.connect();

    return () => {
      ws.destroy();
      wsRef.current = null;
      // Clear the last event ID so the poller treats the WS as inactive.
      // Without this, a stale ref value can cause the poller to skip
      // invalidations for small event gaps while the WS is offline.
      lastEventIdRef.current = null;
      // Drop any pending permissions refresh — it belongs to the connection
      // (and org) being torn down, not the one about to be created.
      if (permissionsChangedTimerRef.current !== null) {
        clearTimeout(permissionsChangedTimerRef.current);
        permissionsChangedTimerRef.current = null;
      }
      setStatus('disconnected');
    };
  }, [queryClient, enabled, orgId, handleEvents, handleFullRefreshRequired]);

  // Reset on org change or when WS is disabled
  useEffect(() => {
    lastEventIdRef.current = null;
  }, [orgId, enabled]);

  return { status, lastEventIdRef, subscribe, unsubscribe, forceReconnect };
}
